import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Blocks, BookOpen, Brain, Gamepad2, GraduationCap, Headphones, Languages, Lightbulb, MailQuestion, Search, Smartphone, Sparkles, TrendingUp, Zap } from "lucide-react";
import { api } from "../api";
import { ProductCard } from "../components/ProductCard";
import { ProductGrid } from "../components/ProductGrid";
import { BookCover } from "../components/BookCover";
import { Reveal } from "../components/Reveal";
import { StatCounter } from "../components/StatCounter";
import { WaveformPlayer } from "../components/WaveformPlayer";
import { QuizSection } from "../components/QuizSection";
import { AppsSection } from "../components/AppsSection";
import { GamesSection, BundleCard } from "../components/GamesSection";
import { PricingCampaign } from "../components/PricingCampaign";
import { BannerCarousel } from "../components/BannerCarousel";
import { PromoStrip } from "../components/PromoStrip";
import { PromoVideo } from "../components/PromoVideo";
import { TutorCampaign } from "../components/TutorCampaign";
import { Testimonials } from "../components/Testimonials";

const ICONS = { BookOpen, Search, Sparkles, TrendingUp, Brain, Lightbulb, Languages, Blocks, GraduationCap };

const CAT_COLORS = {
  "beletrystyka": "#22d3ee", "kryminal-i-thriller": "#fb7185", "sci-fi-i-fantasy": "#c084fc",
  "biznes-i-ekonomia": "#fbbf24", "rozwoj-osobisty": "#34d399", "poradniki": "#a3e635",
  "nauka-jezykow": "#f0abfc", "edukacja-dzieci": "#60a5fa", "korepetytor-ai": "#2dd4bf",
};

const READING = [
  { title: "Sprawa numer siedem", format: "audiobook", progress: 68 },
  { title: "Startup od Zera", format: "ebook", progress: 34 },
  { title: "Kroniki Rozbitego Nieba", format: "audiobook", progress: 82 },
];

function ProgressRing({ value }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
      <circle cx="28" cy="28" r={r} fill="none" stroke="#1e293b" strokeWidth="5" />
      <circle cx="28" cy="28" r={r} fill="none" stroke="url(#ringGrad)" strokeWidth="5" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.22,1,0.36,1)" }} />
      <defs>
        <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#06b6d4" /><stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("wszystkie");
  const heroRef = useRef(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e) => setActive(e.detail);
    window.addEventListener("set-category-filter", h);
    return () => window.removeEventListener("set-category-filter", h);
  }, []);

  const filtered = active === "wszystkie"
    ? products.filter((p) => p.format !== "aplikacja" && !p.id.startsWith("pakiet-")) // katalog: ebooki/audiobooki, pakiety pod siatką
    : active.startsWith("format:")
      ? products.filter((p) => p.format === active.slice(7) && !p.id.startsWith("pakiet-"))
      : active.startsWith("subcat:")
        ? products.filter((p) => p.subcategory === active.slice(7))
        : products.filter((p) => p.category === active);
  const apps = products.filter((p) => p.format === "aplikacja");
  const games = products.filter((p) => p.subcategory === "gry-edukacyjne");
  const bundleEbooki = products.find((p) => p.id === "pakiet-ebooki");
  const bundleAudio = products.find((p) => p.id === "pakiet-audiobooki");
  const fmtPrice = (v) => v.toFixed(2).replace(".", ",").replace(",00", "");
  const bestsellers = products.filter((p) => p.badge === "bestseller");
  const audiobooks = products.filter((p) => p.format === "audiobook").slice(0, 3);
  const FLOATING_IDS = ["cien-nad-wisla", "sila-malych-krokow", "kroniki-rozbitego-nieba"];
  const floating = FLOATING_IDS.map((id) => products.find((p) => p.id === id)).filter(Boolean);

  const onHeroMove = (e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    setParallax({ x: (e.clientX - rect.width / 2) / rect.width, y: (e.clientY - rect.top - rect.height / 2) / rect.height });
  };

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} onMouseMove={onHeroMove} className="grain hero-mist relative flex min-h-[84vh] items-center overflow-hidden bg-slate-950 pb-24 pt-28 sm:pt-32" data-testid="hero-section">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/b348fc99-e0f8-4fe9-be9c-6f9ed6648170/images/00504f8547b5a797892969589afcc1d836e16e7523238eeaf4b99bb972cc0ac5.jpeg"
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{
            transform: `translate(${parallax.x * -18}px, ${parallax.y * -12}px) scale(1.06)`,
            transition: "transform 0.3s ease-out",
          }}
        />
        <div className="pointer-events-none absolute inset-0 hero-fade" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 55% at 50% 0%, rgba(34,211,238,0.28), transparent 65%), radial-gradient(ellipse 60% 50% at 80% 110%, rgba(20,184,166,0.15), transparent 60%)" }} />
        {/* mgła na dole bannera — zamglone przejście zamiast prostej linii */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-52 backdrop-blur-2xl hero-mist-band"
          style={{
            maskImage: "linear-gradient(to bottom, transparent, #000 55%)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent, #000 55%)",
          }}
        />

        {floating.map((p, i) => (
          <motion.div
            key={p.id}
            className="pointer-events-none absolute z-0 hidden w-28 opacity-70 lg:block xl:w-36"
            style={{
              top: `${[18, 55, 30][i % 3]}%`,
              ...(i === 0 ? { left: "4%" } : i === 1 ? { right: "6%" } : { right: "20%" }),
              transform: `translate(${parallax.x * (12 + i * 8)}px, ${parallax.y * (10 + i * 6)}px)`,
            }}
            animate={{ y: [-14, 14], rotate: [-4, 4] }}
            transition={{ duration: 5 + i * 1.3, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
          >
            <BookCover product={p} />
          </motion.div>
        ))}

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-2 rounded-full glass-panel px-4 py-1.5 text-xs font-semibold text-cyan-300">
              <Zap className="h-3.5 w-3.5" /> Ponad 16 000 tytułów dostępnych od ręki
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Twoja biblioteka.<br />
            <span className="gradient-text">Twoje centrum edukacji.</span><br />
            Zawsze przy Tobie.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mx-auto mt-6 max-w-xl text-base text-slate-400 sm:text-lg"
          >
            Ebooki, audiobooki, aplikacje edukacyjne, gry i wirtualni korepetytorzy — jedna platforma dla całej rodziny. Płacisz raz, masz na zawsze.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-cta-catalog"
              onClick={() => document.getElementById("katalog")?.scrollIntoView({ behavior: "smooth" })}
              className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 px-8 py-3.5 font-display text-sm font-bold text-slate-950 shadow-xl shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
            >
              Przeglądaj katalog <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-audio"
              onClick={() => document.getElementById("audiobooki")?.scrollIntoView({ behavior: "smooth" })}
              className="flex items-center gap-2 rounded-full glass-panel px-8 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
            >
              <Headphones className="h-4 w-4" /> Posłuchaj próbki
            </button>
          </motion.div>
        </div>
      </section>

      {/* KONTYNUUJ CZYTANIE */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-display text-xl font-bold sm:text-2xl">Kontynuuj czytanie</h2>
            <span className="text-xs text-slate-500">Twoja ostatnia aktywność</span>
          </div>
        </Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {READING.map((r, i) => (
            <Reveal key={r.title} delay={i * 120}>
              <div className="flex items-center gap-4 rounded-2xl glass-panel p-4 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-cyan-400/40" data-testid={`continue-reading-${i}`}>
                <div className="relative">
                  <ProgressRing value={r.progress} />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-cyan-300">{r.progress}%</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-100">{r.title}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                    {r.format === "audiobook" ? <Headphones className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
                    {r.format === "audiobook" ? "Słuchasz" : "Czytasz"} · zostało {100 - r.progress}%
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* KATEGORIE + KATALOG */}
      <section id="katalog" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 sm:px-6">
        <Reveal>
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Przeglądaj <span className="gradient-text">kategorie</span></h2>
          <p className="mt-2 text-sm text-slate-400">Filtruj na żywo — bez przeładowania strony.</p>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="category-bar">
            {[
              { key: "wszystkie", label: "Cały katalog", desc: `${products.filter((p) => p.format !== "aplikacja" && !p.id.startsWith("pakiet-")).length} produktów`, icon: Sparkles, g: "from-slate-500 to-slate-700", img: "/img/tiles/katalog.webp" },
              { key: "format:ebook", label: "Ebooki", desc: "EPUB · PDF · MOBI", icon: BookOpen, g: "from-cyan-500 to-blue-600", img: "/img/tiles/ebooki.webp" },
              { key: "format:audiobook", label: "Audiobooki", desc: "Słuchaj w drodze", icon: Headphones, g: "from-fuchsia-500 to-purple-600", img: "/img/tiles/audiobooki.webp" },
              { key: "section:aplikacje", label: "Aplikacje", desc: "Interaktywna nauka", icon: Smartphone, g: "from-teal-400 to-emerald-500", img: "/img/tiles/aplikacje.webp" },
              { key: "section:gry", label: "Gry edukacyjne", desc: "Seria DigiLib World", icon: Gamepad2, g: "from-amber-400 to-orange-500", img: "/img/banners/digilib-world-campaign.jpeg" },
            ].map((f, fi) => {
              const Icon = f.icon;
              const isActive = active === f.key;
              return (
                <button
                  key={f.key}
                  data-testid={`category-filter-${f.key.replace(":", "-")}`}
                  onClick={() => f.key.startsWith("section:")
                    ? document.getElementById(f.key.slice(8))?.scrollIntoView({ behavior: "smooth" })
                    : setActive(f.key)}
                  className={`group relative flex items-center gap-3.5 overflow-hidden rounded-2xl glass-panel p-4 text-left transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 ${isActive ? "border-cyan-400/60 shadow-[0_14px_40px_-12px_rgba(6,182,212,0.5)]" : "hover:border-cyan-400/40"}`}
                >
                  <img src={f.img} alt="" aria-hidden="true" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-55 transition-transform duration-500 group-hover:scale-110 group-hover:opacity-70" />
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20" />
                  <span className="tile-shine" />
                  <span className={`tile-icon relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${f.g} text-white shadow-lg transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110`} style={{ animationDelay: `${fi * 0.8}s` }}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="relative z-10">
                    <span className="block text-sm font-bold text-white drop-shadow">{f.label}</span>
                    <span className="block text-[11px] text-slate-300">{f.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {categories.map((c) => {
              const Icon = ICONS[c.icon] || BookOpen;
              const col = CAT_COLORS[c.slug] || "#22d3ee";
              const isActive = active === c.slug;
              return (
                <button
                  key={c.slug}
                  data-testid={`category-filter-${c.slug}`}
                  onClick={() => setActive(c.slug)}
                  className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${isActive ? "text-slate-950 shadow-lg" : "glass-panel text-slate-300 hover:border-cyan-400/40"}`}
                  style={isActive ? { background: `linear-gradient(90deg, ${col}, #22d3ee)` } : {}}
                >
                  <Icon className="h-4 w-4" style={{ color: isActive ? undefined : col }} /> {c.name}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${isActive ? "bg-slate-950/20" : "bg-slate-800"}`}>{c.count}</span>
                </button>
              );
            })}
          </div>
        </Reveal>
        <div key={active} className="mt-8">
          <ProductGrid products={filtered} testid="products-grid" />
        </div>
        {filtered.length === 0 && <p className="mt-10 text-center text-slate-500">Ładowanie katalogu...</p>}

        {/* PAKIETY książkowe — pod produktami danej kategorii */}
        {(bundleEbooki || bundleAudio) && (
          <div className="mt-12 grid gap-6 lg:grid-cols-2" data-testid="book-bundles">
            {bundleEbooki && ["wszystkie", "format:ebook"].includes(active) && (
              <BundleCard
                testid="bundle-ebooki-card"
                img="/img/tiles/ebooki.webp"
                badge={<><BookOpen className="h-3.5 w-3.5" /> Pakiet całej biblioteki</>}
                title="Wszystkie ebooki w jednym pakiecie"
                desc="Cała biblioteka DigiLib: kryminał, romans, fantasy, rozwój i więcej — EPUB, PDF i MOBI na każde urządzenie. Kupujesz raz, czytasz na zawsze."
                items={["9 ebooków", "EPUB · PDF · MOBI", "Bez abonamentu"]}
                price={fmtPrice(bundleEbooki.price)}
                oldPrice={`${fmtPrice(bundleEbooki.old_price)} zł`}
                note="Oszczędzasz ponad 155 zł względem zakupu osobno"
                soon={false}
                href="/produkt/pakiet-ebooki"
              />
            )}
            {bundleAudio && ["wszystkie", "format:audiobook"].includes(active) && (
              <BundleCard
                testid="bundle-audio-card"
                img="/img/tiles/audiobooki.webp"
                badge={<><Headphones className="h-3.5 w-3.5" /> Pakiet audio</>}
                title="Wszystkie audiobooki w jednym pakiecie"
                desc="Cała kolekcja do słuchania — w drodze, na treningu, wieczorem. Z darmowymi próbkami. Kupujesz raz, słuchasz na zawsze."
                items={["7 audiobooków", "Darmowe próbki", "Bez abonamentu"]}
                price={fmtPrice(bundleAudio.price)}
                oldPrice={`${fmtPrice(bundleAudio.old_price)} zł`}
                note="Oszczędzasz ponad 130 zł względem zakupu osobno"
                soon={false}
                href="/produkt/pakiet-audiobooki"
              />
            )}
          </div>
        )}
      </section>

      {/* BANERY */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <BannerCarousel />
        </Reveal>
      </section>

      {/* BESTSELLERY */}
      <section id="bestsellery" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 sm:px-6">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Polecane i <span className="gradient-text">bestsellery</span></h2>
              <p className="mt-2 text-sm text-slate-400">Tytuły, które pokochały tysiące czytelników.</p>
            </div>
          </div>
        </Reveal>
        <div className="mt-8">
          <ProductGrid products={bestsellers} testid="bestsellers-grid" />
        </div>
      </section>

      {/* BANER PROMO: LATO */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <PromoStrip
            testid="promo-strip-lato"
            img="https://static.prod-images.emergentagent.com/jobs/b348fc99-e0f8-4fe9-be9c-6f9ed6648170/images/b308dc611608ce884d52e33f47c5c58d6077622e1bd8559145a9511df43f62a7.jpeg"
            badge="Wakacyjna promocja"
            title="Lato z książką — do -30% na wybrane tytuły"
            subtitle="Hammock, słońce i dobra historia. Zgarnij wakacyjne hity w obniżonych cenach."
            cta="Zobacz promocje"
            target="katalog"
            accent="from-amber-400 to-orange-500"
          />
        </Reveal>
      </section>

      {/* AUDIOBOOKI */}
      <section id="audiobooki" className="relative scroll-mt-24 overflow-hidden py-20">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(6,182,212,0.08), transparent 70%)" }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-teal-500/10 border border-teal-500/30 px-4 py-1.5 text-xs font-semibold text-teal-300">
                  <Headphones className="h-3.5 w-3.5" /> Strefa audiobooków
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                  Posłuchaj, <span className="gradient-text">zanim kupisz</span>
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
                  Każdy audiobook ma darmową próbkę. Włącz fragment poniżej i przekonaj się, jak brzmi Twoja następna historia — w sam raz na dojazd, trening czy wieczorny relaks.
                </p>
                <div className="mt-8">
                  <WaveformPlayer />
                </div>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
              {audiobooks.map((p, i) => (
                <Reveal key={p.id} delay={i * 100}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEKCJA APLIKACJI INTERAKTYWNYCH — po strefie książkowej */}
      <PromoVideo />
      <TutorCampaign />
      <AppsSection apps={apps} />

      {/* GRY EDUKACYJNE — osobna kategoria */}
      <GamesSection games={games} />

      {/* STATYSTYKI */}
      <section className="border-y border-cyan-500/10 bg-slate-900/40 py-16">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-10 px-4 sm:px-6 lg:grid-cols-4" data-testid="stats-section">
          <StatCounter value={145000} suffix="+" label="Sprzedanych ebooków" />
          <StatCounter value={82000} suffix=" h" label="Godzin audiobooków" />
          <StatCounter value={49000} suffix="+" label="Zadowolonych czytelników" />
          <StatCounter value={4.9} suffix="/5" decimals={1} label="Średnia ocena sklepu" />
        </div>
      </section>

      {/* BANER PROMO: ROZWÓJ */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <PromoStrip
            testid="promo-strip-rozwoj"
            img="https://static.prod-images.emergentagent.com/jobs/b348fc99-e0f8-4fe9-be9c-6f9ed6648170/images/d50939970e401c210a19b6c36cd46d195a63b181192303c30439c70c78377bbe.jpeg"
            badge="Kolekcja miesiąca"
            title="Rozwój osobisty, który naprawdę działa"
            subtitle="Nawyki, mindfulness i produktywność — wyselekcjonowane audiobooki do słuchania w drodze."
            cta="Poznaj kolekcję"
            target="audiobooki"
            accent="from-cyan-400 to-blue-500"
          />
        </Reveal>
      </section>

      {/* QUIZ */}
      <section id="quiz" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6">
        <Reveal>
          <QuizSection />
        </Reveal>
      </section>

      {/* KAMPANIA PAKIETÓW */}
      <PricingCampaign />

      {/* WIDGET: ZGŁOŚ KSIĄŻKĘ */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <Reveal>
          <div className="glow-border rounded-3xl p-[2px]" data-testid="book-request-widget">
            <div className="relative overflow-hidden rounded-3xl bg-slate-950/95 px-6 py-12 text-center sm:px-12">
              <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full bg-fuchsia-500/15 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-cyan-500/15 blur-3xl" />
              <div className="relative">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-fuchsia-500 to-cyan-500 shadow-lg shadow-fuchsia-500/30">
                  <MailQuestion className="h-7 w-7 text-white" />
                </div>
                <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
                  Nie znalazłeś książki, <span className="gradient-text">której szukasz?</span>
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
                  Szukasz tematyki lub rodzaju książki, którego nie ma w naszej ofercie, a może nawet na rynku? Pomożemy!
                  Podaj tytuł albo opisz, jaka tematyka Cię interesuje, a w ciągu kilku godzin otrzymasz od nas ofertę
                  na daną książkę lub propozycje tytułów z wybranej przez Ciebie tematyki.
                </p>
                <button
                  data-testid="book-request-widget-cta"
                  onClick={() => window.dispatchEvent(new Event("open-book-request"))}
                  className="mt-8 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 px-9 py-3.5 font-display text-sm font-bold text-white shadow-xl shadow-fuchsia-500/30 transition-transform hover:scale-105 active:scale-95"
                >
                  Opisz, czego szukasz
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* OPINIE */}
      <section className="mx-auto max-w-7xl px-4 pb-24 pt-8 sm:px-6">
        <Reveal>
          <h2 className="text-center font-display text-2xl font-bold tracking-tight sm:text-3xl">Co mówią <span className="gradient-text">czytelnicy</span></h2>
        </Reveal>
        <Reveal delay={120}>
          <div className="mt-12">
            <Testimonials />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
